
import React from 'react';
import { Link } from 'react-router-dom';
import { Play, Eye } from 'lucide-react';
import { Game } from '@/types/game';
import { createGameUrl } from '@/utils/gameUtils';
import { OptimizedImage } from '@/components/ui/optimized-image';

interface GameCardProps {
  game: Game;
  size?: 'small' | 'medium' | 'large';
  priority?: boolean;
  className?: string;
}

const GameCard: React.FC<GameCardProps> = ({ 
  game, 
  size = 'medium', 
  priority = false,
  className = '' 
}) => {
  const displayPlayCount = game.fakePlayCount ?? game.playCount ?? 0;
  const gameUrl = createGameUrl(game.title);

  return (
    <Link 
      to={gameUrl}
      className={`group cursor-pointer block ${className}`}
      onClick={() => {
        window.scrollTo(0, 0);
      }}
    >
      <div className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 h-full flex flex-col transform hover:-translate-y-1 hover:scale-[1.02] animate-fade-in">
        {/* Thumbnail with overlays */}
        <div className="relative flex-shrink-0">
          <OptimizedImage
            src={game.thumbnail}
            alt={game.title}
            className="w-full aspect-square object-cover transition-transform duration-300 group-hover:scale-105"
            priority={priority}
          />
          
          {/* Play overlay */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-50 group-hover:scale-100 bg-white/90 rounded-full p-2">
              <Play className="w-4 h-4 text-blue-600" fill="currentColor" />
            </div>
          </div>

          {/* Top left badges */}
          <div className="absolute top-2 left-2 flex flex-col gap-1">
            {game.isNew && (
              <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1 animate-fade-in">
                ✨ New
              </span>
            )}
            {game.trending && (
              <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1 animate-fade-in">
                🔥 Hot
              </span>
            )}
          </div>

          {/* Rating badge - top right */}
          {game.rating && game.rating > 0 && (
            <div className="absolute top-2 right-2">
              <span className="bg-yellow-400 text-white text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1 animate-fade-in">
                ⭐ {game.rating.toFixed(1)}
              </span>
            </div>
          )}
        </div>

        {/* Content - Fixed height container */}
        <div className="p-3 flex-1 flex flex-col justify-between min-h-[80px]">
          <h3 className="font-semibold text-gray-900 text-sm mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors duration-300 leading-tight">
            {game.title}
          </h3>
          
          <div className="flex items-center justify-between text-xs text-gray-500 mt-auto">
            <span className="text-blue-600 font-medium capitalize group-hover:text-blue-700 transition-colors duration-300">
              {game.category}
            </span>
            {displayPlayCount > 0 && (
              <div className="flex items-center gap-1 group-hover:text-gray-700 transition-colors duration-300">
                <Eye className="w-3 h-3" />
                <span>{displayPlayCount.toLocaleString()}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default GameCard;
